import React, { useMemo } from 'react';

import { EAvailableSpeed, EVerificationStatus } from '@r1-frontend/entities/ContactAddress/initialState';

import { TAlertIcon } from '@r1-frontend/ui-react/components/Alert';
import { TAlertTypes } from '@r1-frontend/ui-react/components/Alert/TAlertTypes';
import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { InfoCircle, TickCircle } from '@r1-frontend/ui-react/components/svg/main';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import { useMatchMediaByWidth } from '@r1-frontend/shared/hooks/useMatchMedia';

import RequestForm from './RequestForm';

import * as ST from './styled';

type TResultTypes = 'notConnected' | 'canConnected' | 'gigabitError' | 'checkError' | 'flatError';

interface IResult {
    getText: (args: { tariffName?: string, availableSpeed: EAvailableSpeed }) => JSX.Element,
    hasCallRequest: boolean,
    messageType: TAlertTypes,
    icon: TAlertIcon,
}

const results: Record<TResultTypes, IResult> = {
    canConnected: {
        getText: ({ tariffName = '' }) => <>По вашему адресу можно подключить тариф «{tariffName}»</>,
        hasCallRequest: false,
        messageType: 'success',
        icon: () => <TickCircle/>,
    },
    notConnected: {
        getText: () => <>Ваш дом пока не&nbsp;подключён к&nbsp;Дом.ру. Можем сообщить вам, когда подключим.
            Чтобы мы&nbsp;смогли дозвониться&nbsp;&mdash; проверьте, что у&nbsp;вас отключен автоответчик и&nbsp;антиспам приложения.</>,
        hasCallRequest: true,
        messageType: 'error',
        icon: () => <InfoCircle/>,
    },
    gigabitError: {
        getText: ({ availableSpeed }) => {
            if (availableSpeed === EAvailableSpeed.between100and1000) {
                return <>
                    Пока в&nbsp;вашем доме можно подключить только тарифы со&nbsp;скоростью меньше <ST.BoldText>1000&nbsp;Мбит/с</ST.BoldText>.
                </>;
            } else {
                return <>
                    Пока в&nbsp;вашем доме можно подключить только тариф с&nbsp;максимальной скоростью <ST.BoldText>100&nbsp;Мбит/с</ST.BoldText>.
                </>;
            }
        },
        hasCallRequest: false,
        messageType: 'info',
        icon: () => <InfoCircle/>,
    },
    checkError: {
        getText: () => <>Не&nbsp;смогли проверить ваш адрес. Пожалуйста, оставьте ваши ФИО и&nbsp;номер телефона&nbsp;&mdash; мы&nbsp;позвоним вам и&nbsp;всё проверим.
            Чтобы мы&nbsp;смогли дозвониться&nbsp;&mdash; проверьте, что у&nbsp;вас отключен автоответчик и&nbsp;антиспам приложения.</>,
        hasCallRequest: true,
        messageType: 'error',
        icon: () => <InfoCircle/>,
    },
    flatError: {
        getText: () => <>Этот тариф недоступен по&nbsp;вашему адресу. Оставьте свои контакты&nbsp;&mdash; мы&nbsp;перезвоним и&nbsp;подберём другой вариант.
            Чтобы мы&nbsp;смогли дозвониться&nbsp;&mdash; проверьте, что у&nbsp;вас отключен автоответчик и&nbsp;антиспам приложения.</>,
        hasCallRequest: true,
        messageType: 'error',
        icon: () => <InfoCircle/>,
    },
};

export interface IAddressCheckMessageProps {
    tariffName: string | undefined,
    gigabitMayConnect: boolean,
    requiredGigaSpeed: boolean,
    isNotFlatAddress?: boolean,
    isHouseConnected: boolean,
    addressStatus: EVerificationStatus,
    availableSpeed: EAvailableSpeed,
}

/**
 * Выводит сообщение с результатом проверки адреса или null, если адрес ещё не проверялся
 */
export const AddressCheckMessage = ({
    tariffName,
    gigabitMayConnect,
    requiredGigaSpeed,
    isNotFlatAddress,
    isHouseConnected,
    addressStatus,
    availableSpeed,
}: IAddressCheckMessageProps): JSX.Element | null => {
    const isGigabitError = requiredGigaSpeed && !gigabitMayConnect;
    const isTariffCanConnect = isHouseConnected && !isGigabitError;
    const isMobile = useMatchMediaByWidth(wideBreakpoints.mobile);
    const resultType = useMemo(() => {
        switch (true) {
            // Проверка адреса не прошла
            case (addressStatus === EVerificationStatus.error):
                return 'checkError';
            // Проверка адреса не прошла
            case (isNotFlatAddress):
                return 'flatError';
            // Тариф можно подключить
            case (isTariffCanConnect):
                return 'canConnected';
            // Дом не подключен
            case (![EVerificationStatus.notVerified, EVerificationStatus.verifying].includes(addressStatus) && !isHouseConnected):
                return 'notConnected';
            // Нет гигабитной сети
            case (isGigabitError):
                return 'gigabitError';
            default:
                return 'checkError';
        }
    }, [isTariffCanConnect, isGigabitError, isHouseConnected, addressStatus]);

    const result = results[resultType];

    // Адрес ещё не проверяли
    if (addressStatus === EVerificationStatus.notVerified || addressStatus === EVerificationStatus.verifying) {
        return null;
    }

    return (
        <ST.Alert Icon={result.icon} type={result.messageType}>
            <Paragraph4 $withIndent={isMobile && !isTariffCanConnect}>
                {result.getText({ tariffName, availableSpeed: availableSpeed })}
            </Paragraph4>
            {result.hasCallRequest &&
                <RequestForm/>
            }
        </ST.Alert>
    );
};
